import { Distance } from '@/types/Distance';
import { FacetOption } from '@/types/FacetOption';
import { FacetRanges } from '@/types/FacetRange';
import { FilterChip } from '@liveauctioneers/caterwaul-components/lib/FilterChip/FilterChip';
import { format, fromUnixTime } from 'date-fns';
import { H6 } from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import { SearchQueryDateRange } from '@/types/search/searchQuery';
import { shortFormatDate } from '@liveauctioneers/caterwaul-components/lib/utils/dates';
import FacetTypes from '@/types/search/enums/facetTypes';
import isEqual from 'react-fast-compare';
import React, { Component, MouseEventHandler } from 'react';
import styled from 'styled-components';

export type SearchFilterChipsProps = {
    distance: Distance;
    facetOptionChecked?: (facetName: string, value: string, isChecked: boolean) => void;
    facets: any[];
    filterTerms?: string[] | null;
    publishDate: SearchQueryDateRange;
    ranges: FacetRanges;
    removeCountryAndDistance?: React.MouseEventHandler;
    removeFacetById?: Function;
    removePublishedDate?: React.MouseEventHandler;
    removeSaleDates?: React.MouseEventHandler;
    saleDateMax?: number;
    saleDateMin?: number;
    selectedCreator?: string;
    shouldShowBuyNowFilter?: boolean;
    showWithCategories?: boolean;
    taxonomyAllSelected?: Function;
    toggleFilterTerm?: Function;
};

export default class SearchFilterChips extends Component<SearchFilterChipsProps> {
    static defaultProps = {
        facets: [],
    };

    shouldComponentUpdate = (nextProps: SearchFilterChipsProps) => !isEqual(this.props, nextProps);

    safePush = (result: any, filters: any) => {
        if (result) {
            filters.push(result);
        }

        return filters;
    };

    renderFilters = () => {
        const filtersWithoutCategories = this.props.facets.filter((facet) => facet.id !== FacetTypes.CATEGORY_ID);
        let filters = [];
        filtersWithoutCategories.forEach((facet) => {
            if (facet.options) {
                filters = this.safePush(this.renderMultiselect(facet.id, facet.options), filters);
            } else if (facet.categories) {
                filters = this.safePush(this.renderTaxonomy(facet), filters);
            }
        });

        return filters;
    };

    renderFilterTerms = () => {
        const filterTerms = this.props.filterTerms || [];
        return filterTerms.map((filterTerm) => (
            <FilterChip
                key={filterTerm}
                onRemove={() => this.props.toggleFilterTerm(filterTerm)}
            >
                <H6 $steel>“{filterTerm}”</H6>
            </FilterChip>
        ));
    };

    renderTaxonomy = (facet: any) => {
        if (!facet.categories.facets || !facet.categories.facets.some(({ selected }) => selected)) {
            return null;
        }

        let taxonomyFunc;
        if (this.props.taxonomyAllSelected) {
            taxonomyFunc = () =>
                this.props.taxonomyAllSelected &&
                this.props.taxonomyAllSelected(facet.id, facet.categories.facets[0].id);
        }

        return (
            <FilterChip
                key={facet.categories.facets[0].id}
                onRemove={taxonomyFunc}
            >
                <H6 $steel>{facet.categories.facets[0].name}</H6>
            </FilterChip>
        );
    };

    renderDistanceFilter = () => {
        const { distance } = this.props;
        if (distance && distance.space && distance.zipCode) {
            return (
                <FilterChip
                    key="distanceFilterChip"
                    onRemove={this.props.removeCountryAndDistance}
                >
                    <H6 $steel>
                        within {distance.space} mi of {distance.zipCode}
                    </H6>
                </FilterChip>
            );
        }

        return null;
    };

    renderRangeFilters = () => {
        const { ranges } = this.props;
        if (ranges && (ranges.currentBid || ranges.estimate || ranges.hammerPrice)) {
            const { currentBid, estimate, hammerPrice } = ranges;
            return (
                <StyledRangeFilterContainer key="rangeFilters">
                    {currentBid && this.renderRangeFilter(currentBid, 'Current Bid:', FacetTypes.CURRENT_BID_ID)}
                    {estimate && this.renderRangeFilter(estimate, 'Estimate:', 'estimate')}
                    {hammerPrice && this.renderRangeFilter(hammerPrice, 'Sold For:', 'hammerPrice')}
                </StyledRangeFilterContainer>
            );
        }

        return null;
    };

    renderRangeFilter = (range: any, displayName: string, facetId: string) => {
        let displayElement;

        if (range.min && range.max) {
            displayElement = `$${range.min} - $${range.max}`;
        } else if (range.max) {
            displayElement = `At Most $${range.max}`;
        } else if (range.min) {
            displayElement = `At Least $${range.min}`;
        }

        let rangeFunc;
        if (this.props.removeFacetById) {
            rangeFunc = () => this.props.removeFacetById && this.props.removeFacetById(facetId);
        }

        return (
            <FilterChip
                key={`${displayName}-${facetId}`}
                onRemove={rangeFunc}
            >
                <H6 $steel>
                    {displayName} {displayElement}
                </H6>
            </FilterChip>
        );
    };

    renderMultiselect = (facetId: string, options: FacetOption[]) => {
        const multiSelectFilters = options
            .filter(({ name, selected }) => {
                if (facetId !== FacetTypes.CREATOR_ID) {
                    return selected;
                }
                return selected && name !== this.props.selectedCreator;
            })
            .map((option) => {
                // hide Buy Now filter chip for users in the buy now quick filter experiment
                if (option.id === 'BuyNow' && this.props.shouldShowBuyNowFilter) {
                    return null;
                }

                let clickHandler: MouseEventHandler;

                if (facetId === FacetTypes.COUNTRY_CODE_ID) {
                    clickHandler = this.props.removeCountryAndDistance;
                } else {
                    clickHandler = () => this.props.facetOptionChecked?.(facetId, option.id, false);
                }

                return (
                    <FilterChip
                        key={option.id}
                        onRemove={clickHandler}
                    >
                        <H6 $steel>{option.name}</H6>
                    </FilterChip>
                );
            });

        return multiSelectFilters.length > 0 ? multiSelectFilters : null;
    };

    renderPublishedDate = (publishedDate: SearchQueryDateRange) => {
        if (publishedDate?.min) {
            const formattedDate = shortFormatDate(publishedDate.min);
            return (
                <FilterChip
                    key="publishedDateFilter"
                    onRemove={this.props.removePublishedDate}
                >
                    <H6 $steel>{formattedDate} - Today </H6>
                </FilterChip>
            );
        }

        return null;
    };

    renderSaleDates = () => {
        const { removeSaleDates, saleDateMax, saleDateMin } = this.props;
        const minYear = saleDateMin ? format(fromUnixTime(saleDateMin), 'yyyy') : null;
        const maxYear = saleDateMax ? format(fromUnixTime(saleDateMax), 'yyyy') : null;
        let dateRangeText: string;
        if (minYear && maxYear) {
            dateRangeText = `${minYear} to ${maxYear}`;
        } else if (minYear) {
            dateRangeText = `since ${minYear}`;
        } else if (maxYear) {
            dateRangeText = `until ${maxYear}`;
        } else {
            return null;
        }

        return (
            <FilterChip
                key="salesDateFilterChip"
                onRemove={removeSaleDates}
            >
                <H6 $steel>{dateRangeText}</H6>
            </FilterChip>
        );
    };

    render() {
        const { publishDate, showWithCategories = false } = this.props;
        const filters = this.renderFilters();
        const filterTerms = this.renderFilterTerms();
        const distanceFilter = this.renderDistanceFilter();
        const rangeFilters = this.renderRangeFilters();
        const publishDateFilter = this.renderPublishedDate(publishDate);
        const saleDateFilters = this.renderSaleDates();

        // Don't render breadcrumbs if there are no filters and isn't the category landing page
        const empty =
            !Boolean(filters.length) &&
            !Boolean(filterTerms.length) &&
            !Boolean(distanceFilter) &&
            !Boolean(rangeFilters) &&
            !Boolean(publishDateFilter) &&
            !Boolean(saleDateFilters);

        if (empty) {
            return null;
        }

        if (!showWithCategories) {
            return [filters, filterTerms, distanceFilter, rangeFilters, publishDateFilter, saleDateFilters];
        }

        return (
            <StyledFiltersWrapper>
                {filterTerms}
                {filters}
                {distanceFilter}
                {rangeFilters}
                {publishDateFilter}
                {saleDateFilters}
            </StyledFiltersWrapper>
        );
    }
}

const StyledFiltersWrapper = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;

    &:empty {
        display: none;
    }
`;

const StyledRangeFilterContainer = styled.div`
    display: flex;
`;
